import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {VitalSignItem} from '@shared/models/vital-sign.model';
import {I18nService} from '@shared/services/i18n.service';
import {VitalSignService} from '@shared/services/vital-sign.service';
import {LoaderService, responsiveBreakpoints, ResponsiveService} from '@core/src/public-api';
import {Subscription} from 'rxjs';

@Component({
  selector: 'si-vit-vital-sign-chart',
  templateUrl: './vit-vital-sign-chart.component.html',
  styleUrls: ['./vit-vital-sign-chart.component.scss']
})
export class VitVitalSignChartComponent implements OnDestroy {
  @Input() sizePage = 9;
  @Input() unit: string;
  @Input() name: string;
  @Input() useLoader: boolean;
  @Input() chartHeight: string;
  @Output() onAverage = new EventEmitter<number>();
  subscriptions: Subscription[] = [];
  chartData: any;
  chartOptions: any;
  labels = [] as string[];
  dataValues = [] as number[];
  isDesktopWidth: boolean;
  isTabletWidth: boolean;
  isMobileWidth: boolean;
  responsiveBreakPoints = responsiveBreakpoints;
  average: number;
  loadedItems: boolean;
  private _vitalSignType: string;

  constructor(
    private responsiveService: ResponsiveService,
    private vitalSignService: VitalSignService,
    private loaderService: LoaderService,
    private i18nService: I18nService
  ) {
    this.setResponsiveLogic();
  }

  get vitalSignType() {
    return this._vitalSignType;
  }

  @Input()
  set vitalSignType(val) {
    this._vitalSignType = val;
    if (val) {
      this.getVitalSigns();
    }
  }

  setResponsiveLogic(): void {
    this.subscriptions.push(
      this.responsiveService.getResponsiveStoreObservable().subscribe(responsiveStore => {
        this.isDesktopWidth = responsiveStore.currentWindowWidth >= this.responsiveBreakPoints.MD;
        this.isTabletWidth = responsiveStore.currentWindowWidth >= this.responsiveBreakPoints.XS && responsiveStore.currentWindowWidth < this.responsiveBreakPoints.MD;
        this.isMobileWidth = responsiveStore.currentWindowWidth < this.responsiveBreakPoints.XS;
        this.initOptions();
      })
    );
  }

  initData(): void {
    const rootStyles = getComputedStyle(document.documentElement);
    this.chartData = {
      labels: this.labels,
      datasets: [
        {
          data: this.dataValues,
          fill: false,
          backgroundColor: rootStyles.getPropertyValue('--color-vital-sign-background').trim(),
          borderColor: rootStyles.getPropertyValue('--color-vital-sign-border').trim(),
          tension: 0,
          pointRadius: 4
        }
      ]
    };
  }

  initOptions(): void {
    this.chartOptions = {
      aspectRatio: this.getAspectRatio(),
      title: {
        display: false
      },
      legend: {
        display: false
      },
      tooltips: {
        callbacks: {
          label: this.formatTooltipLabel.bind(this)
        }
      },
      scales: {
        yAxes: [
          {
            position: 'right',
            ticks: {
              callback: this.formatYAxisValue.bind(this)
            },
            gridLines: {
              display: false
            }
          }
        ],
        xAxes: [
          {
            gridLines: {
              display: true
            }
          }
        ]
      }
    };
  }

  getAspectRatio(): number {
    if (this.isMobileWidth) {
      return 1;
    } else if (this.isTabletWidth) {
      return 1.2;
    } else {
      return 2.2;
    }
  }

  formatTooltipLabel(tooltipItem, data): string {
    return `${this.name}: ${this.formatValueUnit(tooltipItem.yLabel)}`;
  }

  formatValueUnit(value: number): string {
    return `${value} ${this.unit ?? ''}`;
  }

  formatYAxisValue(value: number, index: number, values: number[]): string {
    return this.formatValueUnit(value);
  }

  async getVitalSigns(page = 0, size = this.sizePage): Promise<void> {
    this.setLoader(true);
    try {
      const newVitalSigns = await this.vitalSignService.getVitalSignItems(page, size, this.vitalSignType);
      this.parseVitalSigns(newVitalSigns);
      this.initData();
      this.initOptions();
    } finally {
      this.getAverage(this.dataValues);
      this.setLoader(false);
      this.loadedItems = true;
    }
  }

  setLoader(start: boolean): void {
    if (!this.useLoader) {
      return;
    }

    if (start) {
      this.loaderService.start();
    } else {
      this.loaderService.stop();
    }
  }

  parseVitalSigns(newVitalSigns: VitalSignItem[]): void {
    this.labels = [];
    this.dataValues = [];
    newVitalSigns?.forEach(sign => {
      this.labels.unshift(this.msToString(sign.date));
      this.dataValues.unshift(sign.value);
    });
  }

  msToString(start: number): string {
    const localeKey = this.i18nService.getLanguageShortHyphen();
    const options: Intl.DateTimeFormatOptions = {month: 'short', day: '2-digit', hour: 'numeric', minute: 'numeric'};
    return new Date(start).toLocaleDateString(localeKey, options);
  }

  getAverage(nums: number[]): void {
    if (nums?.length == 0) {
      this.onAverage.emit(null);
      return;
    }

    this.average = nums.reduce((a, b) => (a + b)) / nums.length;
    this.onAverage.emit(this.average);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
